import { useEffect, useRef, useState } from 'react';
import { LOCALES, useI18n } from '@/i18n';

interface LanguageSelectorProps {
  /** `end` anchors the panel to the right edge, `start` to the left. */
  align?: 'start' | 'end';
  /** `up` opens the list above the trigger, for bars at the bottom of the page. */
  drop?: 'down' | 'up';
}

export function LanguageSelector({ align = 'end', drop = 'down' }: LanguageSelectorProps) {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const current = LOCALES.find((item) => item.code === locale) ?? LOCALES[0];

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div className={`lang-switch lang-switch--${align} lang-switch--${drop}`} ref={rootRef}>
      <button
        type="button"
        className="lang-switch__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('lang.select')}
        title={t('lang.label')}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="lang-switch__flag" aria-hidden="true">
          {current.flag}
        </span>
        <span className="lang-switch__code">{current.code.toUpperCase()}</span>
        <svg className="lang-switch__caret" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 10l5 5 5-5z" fill="currentColor" />
        </svg>
      </button>

      {open ? (
        <ul className="lang-switch__menu" role="listbox" aria-label={t('lang.label')}>
          {LOCALES.map((item) => (
            <li key={item.code}>
              <button
                type="button"
                role="option"
                aria-selected={item.code === locale}
                className={`lang-switch__item${item.code === locale ? ' is-selected' : ''}`}
                onClick={() => {
                  setLocale(item.code);
                  setOpen(false);
                }}
              >
                <span className="lang-switch__flag" aria-hidden="true">
                  {item.flag}
                </span>
                <span className="lang-switch__label">{item.label}</span>
                {item.code === locale ? (
                  <svg className="lang-switch__check" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z"
                      fill="currentColor"
                    />
                  </svg>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
